'use client'

import Image from 'next/image'
import {FC} from 'react'

import {EndorsersCarousel} from '@/components/academy/EndorsersCarousel'
import {AcademyHeader} from '@/components/academy/header/AcademyHeader'
import {TopicsCarousel} from '@/components/academy/topics/TopicsCarousel'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {Page} from '@/components/Page'
import {getTmAcademyContent} from '@/content/tm-academy/tmAcademy'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {AcademyContents} from '@/utils/contentful'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

type TmAcademyProps = {contents: AcademyContents; firstArticle: string}

const TmAcademy: FC<React.PropsWithChildren<TmAcademyProps>> = ({contents, firstArticle}) => {
  const {t} = useTranslation()
  const language = useLanguage()
  const {
    hero,
    header,
    topics: {title},
  } = getTmAcademyContent(language)

  const topics = contents.map(({title, slug, keywords}) => ({
    title,
    linkHref: routeTo.tmAcademy.article(slug),
    description: keywords,
  }))

  const mainLinkHref = routeTo.tmAcademy.article(firstArticle)

  return (
    <>
      <Page
        header={
          <AcademyHeader
            contents={contents}
            mainCtaButton={{
              text: t(header.buttonLinkText),
              linkHref: mainLinkHref,
              onClick: () =>
                analytics.track('Click on Trademark academy (Trademark academy - Header)'),
            }}
          />
        }
      >
        <Hero
          title={t(hero.title)}
          subtitle={t(hero.subtitle)}
          primaryButtonLink={mainLinkHref}
          primaryButtonText={t(hero.buttonLinkText)}
          maxWidth="650px"
          alignItems="center"
        >
          <div className="image">
            <Image
              src={hero.imageSrc}
              alt={hero.imageAlt}
              sizes={`${MOBILE_SCREENSIZE} 230px, ${TABLET_SCREENSIZE} 380px, 460px`}
              fill
              style={{objectFit: 'contain'}}
            />
          </div>
        </Hero>
        <Section hasPaddingTop hasPaddingBottom>
          <TopicsCarousel title={t(title)} topics={topics} />
        </Section>
        <Section hasPaddingTop hasPaddingBottom hasBorderTop>
          <EndorsersCarousel />
        </Section>
      </Page>

      <style jsx>{`
        .image {
          position: absolute;
          width: 460px;
          height: 300px;
          right: -${theme.spacing[5]};
        }
        @media ${TABLET_SCREENSIZE} {
          .image {
            position: relative;
            right: 0;
            bottom: 0;
            width: 380px;
            height: 250px;
            margin-left: 0;
            margin-right: 0;
            margin-top: ${theme.spacing[6]};
            margin-bottom: -96px;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 230px;
            height: 150px;
            margin-top: ${theme.spacing[6]};
            margin-bottom: -${theme.spacing[7]};
          }
        }
      `}</style>
    </>
  )
}

export default TmAcademy
